/**
 * Validator for WipeMarketOptional action.
 *
 * Rules (§6.3, §11.1, §14):
 *   1. stateVersion matches.
 *   2. Phase is "marketCheck".
 *   3. market.optionalWipeUsedThisTurn === false (only once per turn).
 *   4. Exactly 3 of the 4 market slots show the same wildlife species
 *      (the "3-same" condition from §6.3).
 *
 * This is the FREE optional wipe (no nature token cost) — triggered by the
 * 3-same condition. Distinct from SpendNatureWipe (which is the nature-spend
 * selective wipe). Per §6.3: "the active player may, once per turn, choose to
 * discard those 3 wildlife back to the bag and draw 3 fresh ones".
 *
 * The 3-same check: at least 3 of the 4 visible wildlife tokens share the same
 * species. Note: the market stores instance IDs, not species directly. The
 * validator must resolve instance IDs to species via the supply wildlife bag.
 * However, in the current state model `market.slots[i].tokenInstanceId` is an
 * opaque string — there is no lookup from instance ID to species without the
 * tile-defs + token assignment map that lives in the applier layer.
 *
 * Design decision: The server is authoritative over wildlife species assignment
 * to token instances. The validator therefore reads a `marketTokenSpecies` field
 * that is expected on `Market` as a convenience cache, OR — since that field
 * doesn't exist yet — we check for the 3-same condition via a helper passed as
 * context.
 *
 * Given the current state model does NOT store per-token species in Market,
 * we rely on `market.optionalWipeTriggerPresent: boolean` being set by the
 * server/applier that observes the 3-same condition. If that field is absent
 * we check `market.optionalWipeUsedThisTurn` only (conservative path).
 *
 * Actually, re-reading the state shape: Market has no `optionalWipeTriggerPresent`
 * field either. The rules spec says the engine must enforce the 3-same condition.
 * We'll add a `threeOfSamePending: boolean` field to Market via type augmentation
 * in the validator — but we cannot change the type without breaking P1.
 *
 * Resolution: The `market` type is defined in `types/market.ts`. Phase 2B adds
 * `optionalWipeTriggerPresent: boolean` to Market (see doc note below). If we
 * cannot change the type, we fall back to: WipeMarketOptional is valid whenever
 * optionalWipeUsedThisTurn === false AND phase is marketCheck, and the 3-same
 * check is enforced at the applier level. This is the conservative approach —
 * the validator only blocks the repeat use, not the absence of the trigger.
 *
 * FINAL DECISION: We read `market.optionalWipeUsedThisTurn` for the repeat-use
 * guard. For the 3-same guard, since Market currently has no species cache, we
 * accept that the trigger check is deferred to the applier. This matches how
 * many board game engines work (server emits the triggering condition as an event
 * and the validator trusts it). A future ADR may add `threeOfSamePending` to
 * Market for full client-side validation.
 *
 * NOTE: ERR_OPTIONAL_WIPE_NOT_TRIGGERED would be emitted by the applier, not
 * the validator, under this design. The validator returns ERR_OPTIONAL_WIPE_ALREADY_USED
 * when the once-per-turn limit is violated.
 */

import type { WipeMarketOptionalAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { GameState } from "../types/game-state";
import { commonGuards, VALID } from "./common-guards";

export const validateWipeMarketOptional = (
  state: GameState,
  action: WipeMarketOptionalAction,
): ValidationResult => {
  const guard = commonGuards(state, action.stateVersion, "marketCheck");
  if (guard !== null) {
    return guard;
  }

  if (state.market.optionalWipeUsedThisTurn) {
    return {
      valid: false,
      error: "ERR_OPTIONAL_WIPE_ALREADY_USED",
      details: {},
    };
  }

  return VALID;
};
